'use client'

import Intercom, { boot, shutdown, update } from '@intercom/messenger-js-sdk'
import { useEffect, useRef } from 'react'

import { useAuth } from '@/features/auth/hooks'

function toIntercomTimestamp(value: string | undefined): number | undefined {
  if (!value || value === '0') return undefined

  const timestamp = Number(value)
  if (!Number.isFinite(timestamp) || timestamp <= 0) return undefined

  return Math.floor(timestamp > 1e12 ? timestamp / 1000 : timestamp)
}

export function IntercomProvider() {
  const { isAuthenticated, user } = useAuth()
  const initializedRef = useRef(false)
  const identityRef = useRef<string | null>(null)

  useEffect(() => {
    const identity = isAuthenticated && user?.userId ? user.userId : null
    const createdAt = toIntercomTimestamp(user?.createdAt)
    const settings = {
      app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
      hide_default_launcher: true,
      // The messenger is Intercom's own iframe, so no app CSS reaches it. These two are
      // the only brand knobs its SDK exposes: `background` for the header, and the one
      // `ox-cyan` accent (STYLE_GUIDE.md) for buttons, links and the user's bubbles.
      background_color: '#0c0c0d',
      action_color: '#18e2f4',
      ...(identity && {
        user_id: identity,
        email: user?.email,
        ...(createdAt && { created_at: createdAt }),
      }),
    }

    if (!initializedRef.current) {
      Intercom(settings)
      initializedRef.current = true
      identityRef.current = identity
      return
    }

    if (identityRef.current !== identity) {
      shutdown()
      boot(settings)
      identityRef.current = identity
      return
    }

    update(settings)
  }, [isAuthenticated, user?.createdAt, user?.email, user?.userId])

  return null
}
